import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';

import { RowsPerPageSelect, type RowsPerPage } from '../../components/RowsPerPageSelect';
import { benchmarkCase, classLine, gradeLabel, NO_CLASS, pageCount, studentName } from './listRows';
import type { ProgressReportStudent, ProgressReportWindow } from './students';
import { LIST } from './tokens';

const CELL_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  p: '8px 16px',
  borderBottom: `1px solid ${LIST.divider}`,
  minWidth: 0,
} as const;

function cellText(weight: 500 | 700) {
  return {
    fontFamily: LIST.font,
    fontSize: 16,
    fontWeight: weight,
    lineHeight: '24px',
    color: LIST.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  } as const;
}

const COLUMNS = {
  select: { width: LIST.columns.select, flexShrink: 0 },
  name: { flex: 1, minWidth: 184 },
  teacher: { width: LIST.columns.teacher, flexShrink: 0 },
  class: { width: LIST.columns.class, flexShrink: 0 },
  grade: { width: LIST.columns.grade, flexShrink: 0 },
  benchmark: { width: LIST.columns.benchmark, flexShrink: 0 },
  preview: { width: LIST.columns.preview, flexShrink: 0 },
} as const;

function Checkbox({
  label,
  checked,
  indeterminate = false,
  onChange,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <Box
      component="input"
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={onChange}
      sx={{ width: 20, height: 20, m: 0, cursor: 'pointer', accentColor: LIST.primary }}
    />
  );
}

const CHIPS = {
  due: { testId: 'due-chip', colors: LIST.dueChip, label: (short: string) => `${short} due` },
  to_score: { testId: 'to-score-chip', colors: LIST.notice, label: () => 'To score' },
  missed: { testId: 'missed-chip', colors: LIST.missedChip, label: (short: string) => `Missed ${short}` },
} as const;

function BenchmarkCell({ student, current }: { student: ProgressReportStudent; current: ProgressReportWindow | null }) {
  const kind = benchmarkCase(student);
  const chip = kind === 'due' || kind === 'to_score' || kind === 'missed' ? CHIPS[kind] : null;
  return (
    <>
      {student.latestBenchmark === null ? (
        <Box component="span" sx={{ ...cellText(500), fontStyle: 'italic', color: LIST.muted }}>
          No benchmark yet
        </Box>
      ) : (
        <Box component="span" sx={cellText(500)}>
          {student.latestBenchmark.label}
        </Box>
      )}
      {chip !== null && current !== null && (
        <Box
          component="span"
          data-testid={chip.testId}
          sx={{
            px: '8px',
            py: '2px',
            borderRadius: '9999px',
            bgcolor: chip.colors.bg,
            color: chip.colors.fg,
            fontFamily: LIST.font,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '16px',
            whiteSpace: 'nowrap',
          }}
        >
          {chip.label(current.short)}
        </Box>
      )}
    </>
  );
}

function PagerButton({ label, disabled, onClick, children }: { label: string; disabled: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        p: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        borderRadius: '8px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color: LIST.ink,
      }}
    >
      {children}
    </Box>
  );
}

export function StudentsTable({
  rows,
  total,
  page,
  current,
  selected,
  sort,
  onToggleSort,
  onToggleRow,
  onTogglePage,
  onPage,
  rowsPerPage,
  onRowsPerPage,
  empty,
}: {
  rows: ProgressReportStudent[];
  total: number;
  page: number;
  current: ProgressReportWindow | null;
  selected: Set<string>;
  sort: 'asc' | 'desc';
  onToggleSort: () => void;
  onToggleRow: (id: string) => void;
  onTogglePage: (select: boolean) => void;
  onPage: (page: number) => void;
  rowsPerPage: RowsPerPage;
  onRowsPerPage: (rowsPerPage: RowsPerPage) => void;
  empty: React.ReactNode;
}) {
  const pages = pageCount(total, rowsPerPage);
  const onPageSelected = rows.filter((row) => selected.has(row.studentId)).length;
  const allSelected = rows.length > 0 && onPageSelected === rows.length;
  const start = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, total);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box data-testid="students-grid" sx={{ minWidth: 1002 }}>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ ...CELL_SX, ...COLUMNS.select }}>
              <Checkbox
                label="Select this page"
                checked={allSelected}
                indeterminate={onPageSelected > 0 && !allSelected}
                onChange={() => onTogglePage(!allSelected)}
              />
            </Box>
            <Box
              role="button"
              tabIndex={0}
              onClick={onToggleSort}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleSort();
                }
              }}
              sx={{ ...CELL_SX, ...COLUMNS.name, cursor: 'pointer', userSelect: 'none' }}
            >
              <Box component="span" sx={{ ...cellText(700), color: LIST.primary }}>
                Student name
              </Box>
              <KeyboardArrowDownIcon sx={{ width: 24, height: 24, color: LIST.primary, transform: sort === 'desc' ? 'rotate(180deg)' : 'none' }} />
            </Box>
            <Box sx={{ ...CELL_SX, ...COLUMNS.teacher }}>
              <Box component="span" sx={cellText(700)}>Teacher</Box>
            </Box>
            <Box sx={{ ...CELL_SX, ...COLUMNS.class }}>
              <Box component="span" sx={cellText(700)}>Class</Box>
            </Box>
            <Box sx={{ ...CELL_SX, ...COLUMNS.grade }}>
              <Box component="span" sx={cellText(700)}>Grade</Box>
            </Box>
            <Box sx={{ ...CELL_SX, ...COLUMNS.benchmark }}>
              <Box component="span" sx={cellText(700)}>Latest benchmark</Box>
            </Box>
            <Box sx={{ ...CELL_SX, ...COLUMNS.preview }} />
          </Box>
          {rows.length === 0 ? (
            empty
          ) : (
            rows.map((row) => {
              const isSelected = selected.has(row.studentId);
              const name = studentName(row);
              return (
                <Box key={row.studentId} data-testid="student-row" sx={{ display: 'flex', bgcolor: isSelected ? LIST.selectedBg : 'transparent' }}>
                  <Box sx={{ ...CELL_SX, ...COLUMNS.select }}>
                    <Checkbox label={`Select ${name}`} checked={isSelected} onChange={() => onToggleRow(row.studentId)} />
                  </Box>
                  <Box sx={{ ...CELL_SX, ...COLUMNS.name }}>
                    <Box component="span" data-testid="student-name" sx={cellText(500)}>
                      {name}
                    </Box>
                  </Box>
                  <Box sx={{ ...CELL_SX, ...COLUMNS.teacher }}>
                    <Box component="span" sx={cellText(500)}>{row.teacher}</Box>
                  </Box>
                  <Box data-testid="student-class" sx={{ ...CELL_SX, ...COLUMNS.class }}>
                    {classLine(row) === '' ? (
                      <Box component="span" sx={{ ...cellText(500), fontStyle: 'italic', color: LIST.muted }}>
                        {NO_CLASS}
                      </Box>
                    ) : (
                      <Box component="span" title={classLine(row)} sx={cellText(500)}>
                        {classLine(row)}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ ...CELL_SX, ...COLUMNS.grade }}>
                    <Box component="span" sx={cellText(500)}>{gradeLabel(row.grade)}</Box>
                  </Box>
                  <Box data-testid="latest-benchmark" sx={{ ...CELL_SX, ...COLUMNS.benchmark }}>
                    <BenchmarkCell student={row} current={current} />
                  </Box>
                  <Box sx={{ ...CELL_SX, ...COLUMNS.preview }}>
                    <Box
                      component={RouterLink}
                      to={`/reports/student-progress/${encodeURIComponent(row.studentId)}`}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: LIST.primary,
                        fontFamily: LIST.font,
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: '24px',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Preview
                      <NavigateNextIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '26px', p: '2px 0' }}>
        <RowsPerPageSelect value={rowsPerPage} onChange={onRowsPerPage} tokens={LIST} />
        <Box component="span" data-testid="page-range" sx={{ fontFamily: LIST.font, fontSize: 12, fontWeight: 500, lineHeight: '16px', color: LIST.muted }}>
          {start}–{end} of {total}
        </Box>
        <Box sx={{ display: 'flex' }}>
          <PagerButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            <ChevronLeftIcon sx={{ width: 24, height: 24 }} />
          </PagerButton>
          <PagerButton label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)}>
            <ChevronRightIcon sx={{ width: 24, height: 24 }} />
          </PagerButton>
        </Box>
      </Box>
    </Box>
  );
}
